import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import UserFormDialog from "@/components/users/UserFormDialog";
import ResetPasswordDialog from "@/components/users/ResetPasswordDialog";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/roles";
import { toast } from "sonner";
import { Pencil, KeyRound, ShieldAlert } from "lucide-react";

const ROLE_CLS = {
  admin: "bg-purple-100 text-purple-800 border-purple-200",
  staff: "bg-blue-100 text-blue-800 border-blue-200",
  doctor: "bg-teal-100 text-teal-800 border-teal-200",
};
const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "Never");

export default function UsersPanel({ doctors, formOpen, setFormOpen }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "User disabled" : "User enabled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doctorName = (id) => doctors.find((d) => d.id === id)?.name;

  return (
    <div className="space-y-4" data-testid="users-panel">
      {users.map((u) => (
        <div key={u.id} data-testid="user-item" className={`bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${u.is_active ? "" : "opacity-60"}`}>
          <div className="h-11 w-11 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-semibold uppercase shrink-0">{(u.name || u.email)[0]}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900">{u.name || "—"}</span>
              <Badge variant="outline" className={ROLE_CLS[u.role]}>{ROLE_LABEL[u.role]}</Badge>
              {u.id === me?.id && <Badge variant="outline">You</Badge>}
              {!u.is_active && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Disabled</Badge>}
              {u.must_change_password && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200"><ShieldAlert className="h-3 w-3 mr-1" />Temp password</Badge>}
            </div>
            <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-4">
              <span>{u.email}</span>
              {u.role === "doctor" && <span>Profile: <b className="text-slate-700">{doctorName(u.doctor_id) || "unlinked"}</b></span>}
              <span>Last login: {fmt(u.last_login)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={() => { setEditing(u); setFormOpen(true); }} data-testid="edit-user-button"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
            <Button size="sm" variant="outline" onClick={() => setResetUser(u)} data-testid="reset-user-password-button"><KeyRound className="h-4 w-4 mr-1" /> Reset</Button>
            <Switch checked={u.is_active} disabled={u.id === me?.id} onCheckedChange={() => toggleActive(u)} data-testid="user-active-switch" />
          </div>
        </div>
      ))}
      <UserFormDialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }} editing={editing} doctors={doctors} onSaved={load} />
      <ResetPasswordDialog user={resetUser} onOpenChange={(v) => !v && setResetUser(null)} onDone={load} />
    </div>
  );
}
